import { useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";
import { FiPlus } from "react-icons/fi";

import Card from "@/components/shared/Card";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import { SASS, STFF } from "@/models/stff";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import SASSEditDialog from "../dialogs/SASSEditDialog";

const columns: GridColumnProps[] = [
  { id: "ProgramYear", field: "ProgramYear", title: "Program Year" },
  { id: "PreschoolName", field: "PreschoolName", title: "School" },
  { id: "LocalStaffId", field: "LocalStaffId", title: "Staff Local ID" },
  {
    id: "ClassroomRole1Name",
    field: "ClassroomRole1Name",
    title: "Classroom Role 1",
  },
  {
    id: "NonClassroomRole1Name",
    field: "NonClassroomRole1Name",
    title: "Non Classroom Role 1",
  },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "Staff Local ID", key: "LocalStaffId" },
  { label: "Classroom Role 1", key: "ClassroomRole1Name" },
  { label: "Non Classroom Role 1", key: "NonClassroomRole1Name" },
];

type DialogState =
  | { mode: "closed" }
  | { mode: "add"; defaults: Partial<SASS> }
  | { mode: "edit"; record: SASS };

export default function SASSRecordsTab({
  sassRecords,
  staff,
}: {
  sassRecords: SASS[];
  staff: STFF;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });

  const closeDialog = () => setDialog({ mode: "closed" });

  const staffName = [
    staff.StaffLegalFirstName,
    staff.StaffLegalMiddleName,
    staff.StaffLegalLastName,
  ]
    .filter(Boolean)
    .join(" ");

  const onAddButtonClick = () => {
    setDialog({
      mode: "add",
      defaults: {
        LocalStaffId: staff.LocalStaffId ?? "",
        PreschoolCode: "",
        ProgramYear: staff.ProgramYear ?? "",
      },
    });
  };

  const onRowClick = (c: SASS) => {
    setDialog({ mode: "edit", record: c });
  };

  const canEdit = leaContext.hasRole("SASSEdit");

  const tableData = sassRecords.map((record) => ({
    ...record,
    StaffName: staffName,
  }));

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3 px-3">
        <TooltipTrigger tooltipText="Export staff assignment records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(
              ["StaffName"],
              "staff-assignment-record"
            )}
          />
        </TooltipTrigger>
        {canEdit && (
          <TooltipTrigger tooltipText="Add a staff assignment record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>
      <Card className="mb-3 shadow-none">
        <KendoTableClientState
          data={tableData}
          columns={columns}
          onRowClick={(event: GridRowClickEvent) => {
            onRowClick(event.dataItem);
          }}
        />
      </Card>

      {dialog.mode === "add" && (
        <SASSEditDialog defaults={dialog.defaults} onClose={closeDialog} />
      )}
      {dialog.mode === "edit" && (
        <SASSEditDialog record={dialog.record} onClose={closeDialog} />
      )}
    </>
  );
}
